import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getParent, types } from 'mobx-state-tree';
import { api } from 'src/api';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { isHttpClientCancel } from 'src/core/services/api/httpClient';
import { createAsync } from 'src/utils/asyncModel';
import { billContentTypeOf } from 'src/utils/billFileType';
import { MAX_BILL_UPLOAD_MB, uploadBillFile } from 'src/stores/billAutomation/uploadBillFile';
import { toUploadResult } from './uploadOutcome';
// Type-only: naming the root for getParent must not create a runtime cycle back to the store index.

/**
 * Where one file has got to on its way up. This is the state of the *upload*, not of the bill —
 * once a file has landed, what happens to it is the bill's own status, read from the bills list so
 * that the modal and the table can never say different things about the same bill.
 */
export const BULK_UPLOAD_STATUS = {
  /** Picked, not yet submitted. The only state in which a file can still be removed. */
  queued: 'queued',
  uploading: 'uploading',
  /** The service created a bill for it. Everything after this is the bill's status. */
  landed: 'landed',
  /** The company already has this file, by content hash. Nothing was created. */
  duplicate: 'duplicate',
  /** Refused before or during the upload. `error` says why, in words meant for the person reading. */
  error: 'error'
};

/**
 * The most files the upload dialog takes at once (LB-6034): the page's own safety cap. Each file goes up and is
 * registered on a request of its own, and none of those routes limits how many files an upload holds.
 */
export const MAX_BULK_UPLOAD_FILES = 100;

/**
 * The largest bill this page accepts, and what the drop zone tells people. The same number apply
 * billing uses, and kept in one place so the two screens cannot come to promise different things.
 */
export const MAX_BULK_UPLOAD_FILE_MB = MAX_BILL_UPLOAD_MB;

/** Mirrors BillFileValidator's allowed extensions. The service is the gate; this is the courtesy. */
export const ACCEPTED_BILL_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.gif,.webp';

/**
 * Files sent at once. One request per file is what makes each one land, fail, or duplicate on its
 * own; a window keeps a large upload from sending all of them at the same time.
 */
const UPLOAD_CONCURRENCY = 3;
const SETTLED_STATUSES = [BULK_UPLOAD_STATUS.landed, BULK_UPLOAD_STATUS.duplicate, BULK_UPLOAD_STATUS.error];

/**
 * The File objects themselves, held outside the tree: a snapshot has to stay serialisable, and a
 * File is not. The entry id is the key, so an entry leaving the queue takes its file with it.
 */
const filesById = new Map();
let nextEntryId = 0;

/**
 * Names one upload, for the service to group its files by.
 *
 * Unguessable on purpose: the service resolves it within the uploading company, so two uploads
 * that shared a value there would be reported as one event. `randomUUID` needs a secure context,
 * which every environment this runs in is, but it is absent under some test runners — and a name
 * that collides is worse than no name, so falling back to a random string beats falling back to
 * something predictable.
 */
const newUploadKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return "upload-".concat(Date.now().toString(36), "-").concat(Math.random().toString(36).slice(2, 12));
};
const getCurrentCompanyId = () => localStorage.getItem(USER_SETTINGS_KEYS.company) || '';

/** One file's journey. The file itself lives in {@link filesById} under the same id. */
const BulkUploadEntry = types.model('BulkUploadEntry', {
  id: types.identifier,
  fileName: types.optional(types.string, ''),
  /** Bytes, as the picker reports them, so two pickings of one file compare exactly. */
  fileSizeBytes: types.optional(types.number, 0),
  status: types.optional(types.string, BULK_UPLOAD_STATUS.queued),
  /**
   * Whether the user has asked for this file to go up. A file added while an earlier batch is
   * still uploading must not be swept along with it, so the drain claims only what was submitted.
   */
  isReady: types.optional(types.boolean, false),
  /** Bytes of the request body sent so far, for the progress bar. */
  sentBytes: types.optional(types.number, 0),
  /**
   * Which upload this file is part of, for the service to group them by (LB-5862).
   *
   * Each file goes up in its own request so that one failing does not take the rest down, which
   * leaves the service no way to tell from a request which files were one thing the person did.
   * Empty for an upload of a single file: that forms no acquisition event, and did not before.
   */
  uploadKey: types.optional(types.string, ''),
  /**
   * The note for our team typed when this file was submitted (LB-6056). Held per file rather than per upload: a
   * single file has no upload key, and files added while an earlier batch goes up are submitted with their own.
   */
  note: types.optional(types.string, ''),
  /** The bill this file became, once it landed. Its status is read from the bills list. */
  billId: types.optional(types.string, ''),
  error: types.optional(types.string, '')
}).views(store => ({
  get fileSizeMB() {
    return store.fileSizeBytes / 1048576;
  },
  get sentMB() {
    return store.sentBytes / 1048576;
  },
  /** 0–1. Falls back to nothing sent when the body size is unknown. */
  get progress() {
    if (!store.fileSizeBytes) return 0;
    return Math.min(store.sentBytes / store.fileSizeBytes, 1);
  },
  /** Nothing more will happen to this file without the user picking it again. */
  get isSettled() {
    return SETTLED_STATUSES.includes(store.status);
  },
  /** Only a file not yet submitted can still be taken out. */
  get isEditable() {
    return store.status === BULK_UPLOAD_STATUS.queued && !store.isReady;
  },
  /** Submitted and waiting for a free upload slot. */
  get isReadyToSend() {
    return store.status === BULK_UPLOAD_STATUS.queued && store.isReady;
  }
})).actions(store => ({
  markReady(uploadKey) {
    let note = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    store.isReady = true;
    store.uploadKey = uploadKey;
    store.note = note;
  },
  markUploading() {
    store.status = BULK_UPLOAD_STATUS.uploading;
    store.error = '';
    store.sentBytes = 0;
  },
  setSentBytes(sentBytes) {
    store.sentBytes = sentBytes;
  },
  markLanded(billId) {
    store.status = BULK_UPLOAD_STATUS.landed;
    store.billId = billId;
  },
  markDuplicate() {
    store.status = BULK_UPLOAD_STATUS.duplicate;
  },
  markError(message) {
    store.status = BULK_UPLOAD_STATUS.error;
    store.error = message;
  }
}));

/**
 * Drains the queue, one request per file, {@link UPLOAD_CONCURRENCY} at a time.
 *
 * Runs on the store rather than in the modal so that closing the modal does not stop it — the PRD
 * asks for uploads that carry on in the background, and a component-local upload cannot do that.
 * A file that fails is recorded against its own entry and never rethrown: one bad file must not
 * take the rest of the batch down with it, and the row's own status is the report, not a toast.
 */
const processQueueAsync = createAsync(() => {
  return async function processQueue(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;

    // The uploads this drain has carried files for, so they can be closed once it is done. Held
    // here rather than read back off the entries at the end, because an entry can be taken out of
    // the queue before then and would take its key with it.
    const uploadKeysSeen = new Set();
    const uploadOne = async entryId => {
      const file = filesById.get(entryId);
      if (!file) {
        this.failEntry(entryId, 'The file is no longer available. Attach it again.');
        return;
      }
      const uploadKey = this.uploadKeyOf(entryId);
      if (uploadKey) uploadKeysSeen.add(uploadKey);

      // A presigned upload signs a content type into the URL, so one has to be named before the
      // file moves rather than read off the bytes once they arrive. The picker accepts by
      // extension, so a file reaching here with no type we know is one that was renamed.
      const contentType = billContentTypeOf(file);
      if (!contentType) {
        this.failEntry(entryId, 'That file type cannot be uploaded.');
        return;
      }
      try {
        // Asked for, sent straight to storage, registered by key -- see uploadBillFile for why the
        // bytes cannot travel through the API. Shared with apply billing, which uploads the same way.
        const _await$uploadBillFile = await uploadBillFile(_objectSpread(_objectSpread(_objectSpread({
            file,
            contentType,
            channel: 'Portal'
          }, uploadKey ? {
            uploadKey
          } : {}), this.noteOf(entryId) ? {
            note: this.noteOf(entryId)
          } : {}), {}, {
            // The file itself now rather than a multipart body wrapped around it, so the bar no
            // longer runs slightly ahead of the bytes.
            onUploadProgress: loadedBytes => this.setEntrySentBytes(entryId, loadedBytes)
          }), options),
          bill = _await$uploadBillFile.bill,
          refusal = _await$uploadBillFile.refusal;
        if (!bill && refusal) {
          this.failEntry(entryId, refusal);
          return;
        }
        this.applyUploadResult(entryId, toUploadResult(bill, file.name));
      } catch (error) {
        // A cancelled request is the queue being torn down, not a file that failed.
        if (isHttpClientCancel(error)) throw error;
        this.failEntry(entryId, 'Upload failed. Try this file again.');
      }
    };

    // Each worker takes the next submitted entry until none is left, so a slow file holds up only
    // its own slot. Claiming marks the entry, which is what stops two workers taking the same one.
    const worker = async () => {
      for (;;) {
        const entryId = this.claimNextQueuedEntry();
        if (!entryId) return;
        await uploadOne(entryId);
      }
    };

    // More files can be submitted while the last of a round is still going up, and by then every
    // worker may already have found nothing left and stopped. Going round again picks those up;
    // without it they would sit waiting behind a drain that is still marked in progress.
    for (;;) {
      await Promise.all(Array.from({
        length: UPLOAD_CONCURRENCY
      }, worker));
      if (this.readyQueuedCount === 0) break;
    }

    // Every file of every upload this drain carried is now settled, so each of those uploads can be
    // told it has no more files coming — which is what lets its summary be reported as one email
    // rather than one per bill.
    //
    // Sent and not waited for, and that is load-bearing rather than tidiness. Awaiting would hold
    // the drain open across a round trip nobody is waiting on — and `startProcessing` does nothing
    // but mark entries ready while a drain is in progress, so a press of Upload arriving in that
    // window would leave its files ready with no drain left to take them. The loop above is what
    // catches files submitted late, and it has already run by here.
    //
    // try/catch rather than .catch(): a synchronous throw is not a rejection, and an unhandled one
    // would take the table refresh below with it — the only part of this the person can see.
    const sealQuietly = async uploadKey => {
      try {
        await api.BillAutomationBills.sealBillsUpload({
          companyId,
          uploadKey
        }, options);
      } catch (_unused) {
        // Nothing to tell them: their files went up, and the sweep closes what this did not.
      }
    };
    uploadKeysSeen.forEach(uploadKey => {
      void sealQuietly(uploadKey);
    });

    // The list is what every row's status is read from, so it is read once the files are up. The
    // Bills page keeps reading it on its own while anything is still extracting.
    //
    // Re-reads whatever the list is currently showing (LB-5828, LB-5842): an unconditional read here
    // would overwrite a batch-filtered view with the whole company's first page, and rebuilding the
    // query would now also drop whatever filters and sort the person had set.
    if (this.landedCount > 0) {
      const companyBills = getParent(this).companyBills;
      companyBills.refetchCurrent();
      // The tiles too, and not only so they are current: the Bills page arms its refresh off
      // `hasBillsInProgress`, which reads the summary once one has landed. Without this the bills
      // just uploaded are in Processing according to the server and settled according to the
      // figures, so nothing starts polling and they sit at Processing until somebody reloads.
      companyBills.getCompanyBillsSummaryAsync.run(undefined, []);
    }
    return undefined;
  };
});

/**
 * The bulk-upload queue. Deliberately a store rather than component state: the upload modal is
 * closeable while files are still going up, so the queue has to outlive it. Runtime only — a page
 * reload loses it, and nothing here is treated as a persisted record.
 */
const BulkUploadStore = types.model('BulkUploadStore', {
  entries: types.optional(types.array(BulkUploadEntry), []),
  processQueueAsync
}).views(store => ({
  /** Files picked but not yet submitted — what the upload button acts on. */
  get queuedCount() {
    return store.entries.filter(entry => entry.isEditable).length;
  },
  /** Files submitted and still waiting for a slot. */
  get readyQueuedCount() {
    return store.entries.filter(entry => entry.isReadyToSend).length;
  },
  get landedCount() {
    return store.entries.filter(entry => entry.status === BULK_UPLOAD_STATUS.landed).length;
  },
  /** Files neither settled nor still on the picker — what the Bills button counts while shut. */
  get inFlightCount() {
    return store.entries.filter(entry => !entry.isSettled && !entry.isEditable).length;
  },
  get hasEntries() {
    return store.entries.length > 0;
  },
  /** How many more files the queue will take before it reaches the cap. */
  get remainingCapacity() {
    return Math.max(MAX_BULK_UPLOAD_FILES - store.entries.length, 0);
  }
})).actions(store => ({
  /**
   * Takes what the picker gave. A file already queued is ignored, one larger than the service
   * accepts is kept as a failed row rather than dropped silently — being told which file was too
   * big beats a count of how many were — and anything past the cap is turned away.
   */
  addFiles(files) {
    const isAlreadyQueued = file => store.entries.some(entry => entry.fileName === file.name && entry.fileSizeBytes === file.size);
    const fresh = files.filter(file => !isAlreadyQueued(file));
    const accepted = fresh.slice(0, store.remainingCapacity);
    let oversized = 0;
    accepted.forEach(file => {
      nextEntryId += 1;
      const id = "bulk-upload-".concat(nextEntryId);
      const isTooLarge = file.size > MAX_BULK_UPLOAD_FILE_MB * 1048576;
      if (isTooLarge) oversized += 1;else filesById.set(id, file);
      store.entries.push({
        id,
        fileName: file.name,
        fileSizeBytes: file.size,
        status: isTooLarge ? BULK_UPLOAD_STATUS.error : BULK_UPLOAD_STATUS.queued,
        error: isTooLarge ? "Larger than ".concat(MAX_BULK_UPLOAD_FILE_MB, " MB") : ''
      });
    });
    return {
      added: accepted.length - oversized,
      oversized,
      rejected: fresh.length - accepted.length
    };
  },
  /** Only a file that has not been sent can be taken out; one in flight is no longer the UI's. */
  removeEntry(entryId) {
    const entry = store.entries.find(item => item.id === entryId);
    if (!entry || !entry.isEditable && !entry.isSettled) return;
    filesById.delete(entryId);
    store.entries.remove(entry);
  },
  /** Which upload a submitted entry belongs to, or empty when it belongs to none. */
  uploadKeyOf(entryId) {
    var _store$entries$find;
    return ((_store$entries$find = store.entries.find(item => item.id === entryId)) === null || _store$entries$find === void 0 ? void 0 : _store$entries$find.uploadKey) || '';
  },
  noteOf(entryId) {
    var _store$entries$find2;
    return ((_store$entries$find2 = store.entries.find(item => item.id === entryId)) === null || _store$entries$find2 === void 0 ? void 0 : _store$entries$find2.note) || '';
  },
  /** Marks the next submitted entry as uploading and hands back its id, or nothing when none is left. */
  claimNextQueuedEntry() {
    const entry = store.entries.find(item => item.isReadyToSend);
    if (!entry) return null;
    entry.markUploading();
    return entry.id;
  },
  setEntrySentBytes(entryId, sentBytes) {
    var _store$entries$find3;
    (_store$entries$find3 = store.entries.find(item => item.id === entryId)) === null || _store$entries$find3 === void 0 || _store$entries$find3.setSentBytes(sentBytes);
  },
  applyUploadResult(entryId, result) {
    const entry = store.entries.find(item => item.id === entryId);
    if (!entry) return;
    if (!result) {
      entry.markError('The service did not report an outcome for this file.');
      return;
    }
    if (result.duplicate) {
      entry.markDuplicate();
      filesById.delete(entryId);
      return;
    }
    if (result.error || !result.billId) {
      entry.markError(result.error || 'Upload failed. Try this file again.');
      return;
    }
    entry.markLanded(result.billId);

    // Registering the bill in the session store is what lets the details view preview the file
    // without a round trip, and what the deep link and the table's own row share. No property is
    // sent with it: the pipeline matches the bill to one from its service address, and a bill it
    // cannot place lands in review rather than being guessed at here.
    const file = filesById.get(entryId);
    getParent(store).uploadedBills.addUploadedBill({
      billId: result.billId,
      fileName: result.fileName || entry.fileName,
      fileUrl: file ? URL.createObjectURL(file) : '',
      fileSizeMB: entry.fileSizeMB
    });
    filesById.delete(entryId);
  },
  failEntry(entryId, message) {
    var _store$entries$find4;
    (_store$entries$find4 = store.entries.find(item => item.id === entryId)) === null || _store$entries$find4 === void 0 || _store$entries$find4.markError(message);
  },
  /**
   * Submits everything picked so far and starts draining.
   *
   * A drain already running is left alone rather than restarted: re-running would cancel the
   * requests in flight, because the async model cancels the previous call of the same action.
   * It picks the newly submitted files up on its next pass instead.
   */
  startProcessing() {
    let note = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    if (store.queuedCount === 0) return undefined;

    // One press of Upload is one thing the person did, so it is one acquisition event and one
    // summary email (LB-5862). Minted per press rather than per drain: files added while an
    // earlier press is still going up are a second thing they did, and get their own.
    //
    // Only when the press carries more than one file. A single file forms no event today — its own
    // notification already is the event's — and sending a key would change the email for the
    // commonest upload there is.
    const uploadKey = store.queuedCount > 1 ? newUploadKey() : '';

    // The note goes with every file of the press, as one email's note goes with every bill it carried.
    store.entries.filter(entry => entry.isEditable).forEach(entry => entry.markReady(uploadKey, note.trim()));
    // Nothing awaits this in the UI — that is the point, the modal is free to close. It is
    // returned so a test can wait for the drain it started.
    if (store.processQueueAsync.inProgress) return undefined;
    return store.processQueueAsync.run(undefined, []);
  },
  /** Drops what has finished and keeps what has not, which is what closing the modal leaves behind. */
  clearSettledEntries() {
    store.entries.filter(entry => entry.isSettled).forEach(entry => {
      filesById.delete(entry.id);
      store.entries.remove(entry);
    });
  },
  /**
   * Takes out everything that has not been sent yet and forgets what has finished. A file already
   * on its way is left alone: the request cannot be unsent, and a bill created by it would
   * otherwise be one nothing in the UI ever mentions again.
   */
  cancelQueue() {
    store.entries.filter(entry => entry.isEditable || entry.isReadyToSend || entry.isSettled).forEach(entry => {
      filesById.delete(entry.id);
      store.entries.remove(entry);
    });
  }
}));
export const bulkUpload = types.optional(BulkUploadStore, {});